/**
 * posOfflineSave — offline-capable POS save path.
 *
 * Mirrors `savePOSTransactionFast` from posFastWriteHelper.js but writes to
 * Dexie + sync_outbox instead of Firestore. Returns the same shape so
 * callers (POSScreen) don't care which path ran.
 *
 * Document numbering: uses the pre-allocated range from posDocCounterRange.js
 * (Decision 1).
 *
 * GL & stockFlow rows: built using the EXACT same logic as posFastWriteHelper
 * to keep on-screen, on-receipt, and on-cloud-sync data identical.
 */

import { SYSTEM_ACCOUNTS, getTranType } from '../config/tranTypes';
import { enrichLineWithUnit, twoUnitsModuleOn, twoUnitLineFields, roundBaseQty } from './unitConversion';
import { getNextDocNumber } from './posDocCounterRange';
import { savePOSSaleLocally, readCollection, readDocById } from './posLocalCache';
import { localDateStr } from './localDate';

// Show Two Units (module item_two_units): the same qty in the item's second
// unit, saved on the line for receipts, views and the item ledger. Empty when
// the module is off or the item has no pair, so those saves are unchanged.
const twoUnitsFor = (line, catalog) => {
  if (!twoUnitsModuleOn() || !line || !line.itemId) return {};
  const item = (catalog || []).find(i => i.id === line.itemId);
  return twoUnitLineFields(item, line) || {};
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const strip = (obj) => {
  if (obj == null) return obj;
  if (Array.isArray(obj)) return obj.map(strip).filter(v => v !== undefined);
  if (typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    out[k] = (v && typeof v === 'object') ? strip(v) : v;
  }
  return out;
};

// Tiny ID generator — deterministic local IDs that won't collide with
// Firestore's auto-IDs. Firestore IDs are 20-char base62; ours start with
// 'local-' so they're visually distinct in any debug tool.
const localId = () => 'local-' + Math.random().toString(36).slice(2, 12) +
                       Math.random().toString(36).slice(2, 10);

// ─── GL entry builders (mirror posFastWriteHelper) ────────────────────

const buildPOSSaleEntries = (base, data, sysAccts) => {
  const entries = [];
  const { totalAmount, taxAmount, subtotal, discountAmount, lines,
    posPayments, paymentMethod, partyAccountId, partyAccountCode, partyName } = data;
  const netAmount = (subtotal || 0) - (discountAmount || 0);

  if (posPayments && posPayments.length > 0) {
    for (const pmt of posPayments) {
      if (!(pmt.amount > 0)) continue;
      if (pmt.method === 'credit' && partyAccountId) {
        entries.push({ ...base, id: localId(),
          accountId: partyAccountId, accountCode: partyAccountCode,
          accountName: partyName ? `Customer: ${partyName}` : 'Customer Account',
          debitAmount: round2(pmt.amount), creditAmount: 0,
          description: `POS credit sale ${base.docNumber}` });
      } else if (pmt.accountId) {
        entries.push({ ...base, id: localId(),
          accountId: pmt.accountId, accountCode: pmt.accountCode || null,
          accountName: pmt.accountName || 'Cash/Bank',
          debitAmount: round2(pmt.amount), creditAmount: 0,
          description: `POS ${pmt.method} payment ${base.docNumber}` });
      }
    }
  } else if (paymentMethod === 'credit' && partyAccountId) {
    entries.push({ ...base, id: localId(),
      accountId: partyAccountId, accountCode: partyAccountCode,
      accountName: partyName ? `Customer: ${partyName}` : 'Customer Account',
      debitAmount: round2(totalAmount), creditAmount: 0,
      description: `POS credit sale ${base.docNumber}` });
  } else {
    const cash = sysAccts[SYSTEM_ACCOUNTS.CASH_IN_HAND];
    if (cash && totalAmount > 0) {
      entries.push({ ...base, id: localId(),
        accountId: cash.id, accountCode: cash.code, accountName: cash.name,
        debitAmount: round2(totalAmount), creditAmount: 0,
        description: `POS cash sale ${base.docNumber}` });
    }
  }

  if (netAmount > 0) {
    const sales = sysAccts[SYSTEM_ACCOUNTS.SALES_GENERAL];
    if (sales) entries.push({ ...base, id: localId(),
      accountId: sales.id, accountCode: sales.code, accountName: sales.name,
      debitAmount: 0, creditAmount: round2(netAmount),
      description: `POS sales ${base.docNumber}` });
  }

  if (taxAmount > 0) {
    const gst = sysAccts[SYSTEM_ACCOUNTS.GST_OUTPUT];
    if (gst) entries.push({ ...base, id: localId(),
      accountId: gst.id, accountCode: gst.code, accountName: gst.name,
      debitAmount: 0, creditAmount: round2(taxAmount),
      description: `GST Output on POS ${base.docNumber}` });
  }

  const totalCOGS = (lines || []).reduce((sum, l) => {
    if (l.itemType === 'service') return sum;
    return sum + (Number(l.quantity) || 0) * (Number(l.unitCost) || 0);
  }, 0);
  if (totalCOGS > 0) {
    const cogs = sysAccts[SYSTEM_ACCOUNTS.COGS];
    const inv = sysAccts[SYSTEM_ACCOUNTS.INVENTORY_CONTROL];
    if (cogs) entries.push({ ...base, id: localId(),
      accountId: cogs.id, accountCode: cogs.code, accountName: cogs.name,
      debitAmount: round2(totalCOGS), creditAmount: 0,
      description: `COGS for POS ${base.docNumber}` });
    if (inv) entries.push({ ...base, id: localId(),
      accountId: inv.id, accountCode: inv.code, accountName: inv.name,
      debitAmount: 0, creditAmount: round2(totalCOGS),
      description: `Inventory issued POS ${base.docNumber}` });
  }
  return entries;
};

const buildPOSReturnEntries = (base, data, sysAccts) => {
  const entries = [];
  const { totalAmount, taxAmount, subtotal, discountAmount, lines,
    partyAccountId, partyAccountCode, partyName, posPayments } = data;
  const netAmount = (subtotal || 0) - (discountAmount || 0);
  const customerName = partyName ? `Customer: ${partyName}` : 'Customer Account';

  if (netAmount > 0) {
    const sr = sysAccts[SYSTEM_ACCOUNTS.SALES_RETURNS];
    if (sr) entries.push({ ...base, id: localId(),
      accountId: sr.id, accountCode: sr.code, accountName: sr.name,
      debitAmount: round2(netAmount), creditAmount: 0,
      description: `Sales return ${base.docNumber}` });
  }
  if (taxAmount > 0) {
    const gst = sysAccts[SYSTEM_ACCOUNTS.GST_OUTPUT];
    if (gst) entries.push({ ...base, id: localId(),
      accountId: gst.id, accountCode: gst.code, accountName: gst.name,
      debitAmount: round2(taxAmount), creditAmount: 0,
      description: `GST Output reversal ${base.docNumber}` });
  }

  let creditedTotal = 0;
  if (posPayments && posPayments.length > 0) {
    for (const pmt of posPayments) {
      if (!(pmt.amount > 0)) continue;
      if (pmt.method === 'credit' && partyAccountId) {
        entries.push({ ...base, id: localId(),
          accountId: partyAccountId, accountCode: partyAccountCode, accountName: customerName,
          debitAmount: 0, creditAmount: round2(pmt.amount),
          description: `Credit note ${base.docNumber}` });
      } else if (pmt.accountId) {
        entries.push({ ...base, id: localId(),
          accountId: pmt.accountId, accountCode: pmt.accountCode || null,
          accountName: pmt.accountName || 'Cash/Bank',
          debitAmount: 0, creditAmount: round2(pmt.amount),
          description: `POS ${pmt.method} refund ${base.docNumber}` });
      }
      creditedTotal += Number(pmt.amount) || 0;
    }
  }
  const remainder = round2((totalAmount || 0) - creditedTotal);
  if (remainder > 0 && partyAccountId) {
    entries.push({ ...base, id: localId(),
      accountId: partyAccountId, accountCode: partyAccountCode, accountName: customerName,
      debitAmount: 0, creditAmount: remainder,
      description: `Credit note ${base.docNumber}` });
  }

  const totalCOGS = (lines || []).reduce((sum, l) => {
    if (l.itemType === 'service') return sum;
    return sum + (Number(l.quantity) || 0) * (Number(l.unitCost) || 0);
  }, 0);
  if (totalCOGS > 0) {
    const inv = sysAccts[SYSTEM_ACCOUNTS.INVENTORY_CONTROL];
    const cogs = sysAccts[SYSTEM_ACCOUNTS.COGS];
    if (inv) entries.push({ ...base, id: localId(),
      accountId: inv.id, accountCode: inv.code, accountName: inv.name,
      debitAmount: round2(totalCOGS), creditAmount: 0,
      description: `Inventory returned ${base.docNumber}` });
    if (cogs) entries.push({ ...base, id: localId(),
      accountId: cogs.id, accountCode: cogs.code, accountName: cogs.name,
      debitAmount: 0, creditAmount: round2(totalCOGS),
      description: `COGS reversal for ${base.docNumber}` });
  }
  return entries;
};

const buildStockFlowRows = (base, data, items, defaultLocation) => {
  const direction = data.tranTypeCode === 'PS' ? 'out' : 'in';
  const locationId = data.locationId || defaultLocation?.id || null;
  const locationName = data.locationName || defaultLocation?.locationName || 'Main';

  const validLines = (data.lines || []).filter(l => l.itemId && l.itemType !== 'service');
  return validLines.map((line, idx) => {
    const item = items.find(i => i.id === line.itemId) || {};
    const enriched = enrichLineWithUnit(item, line);
    const enteredQuantity = Number(line.quantity) || 0;
    const enteredUnitPrice = Number(line.unitPrice) || 0;
    const enteredUnitCost = Number(line.unitCost) || 0;
    const enteredUnitCode = line.unitCode || line.unitOfMeasure || '';
    const conversion = Number(enriched.conversion) || 1;
    // Show Two Units: exact base qty (14.400000001 → 14.4) + second unit for the ledger.
    const two = line.secondUnitCode ? { secondUnitCode: line.secondUnitCode, secondQuantity: Number(line.secondQuantity) || 0 } : null;
    const quantity = two ? roundBaseQty(Number(enriched.baseQuantity) || enteredQuantity) : (Number(enriched.baseQuantity) || enteredQuantity);
    const baseUnitCost = Number(enriched.baseUnitCost) || enteredUnitCost;
    const baseUnitPrice = Number(enriched.baseUnitPrice) || enteredUnitPrice;
    const baseUnit = enriched.baseUnitCode || item.unitOfMeasure || enteredUnitCode || '';

    return {
      id: localId(),
      transactionId: base.transactionId,
      tranTypeCode: data.tranTypeCode,
      docNumber: base.docNumber,
      docDate: data.docDate,
      financialYearId: data.financialYearId,
      branchId: data.branchId || null,
      branchName: data.branchName || null,
      locationId,
      locationName,
      status: data.status || 'Posted',
      direction,
      itemId: line.itemId,
      itemCode: line.itemCode || '',
      itemName: line.itemName || '',
      description: line.description || '',
      quantity,
      unitCost: baseUnitCost,
      unitPrice: baseUnitPrice,
      lineTotal: round2(quantity * baseUnitCost),
      // INV-03 fix (2026-09-30): stock value reports and the running average
      // read totalCost — POS rows never had it, so POS sales never reduced
      // stock value and POS returns diluted the average cost.
      totalCost: round2(quantity * baseUnitCost),
      unitOfMeasure: baseUnit,
      enteredQuantity, enteredUnitPrice, enteredUnitCost, enteredUnitCode, conversion,
      ...(two || {}),
      lineNumber: idx + 1,
      createdAt: base.createdAt,
      createdBy: base.createdBy
    };
  });
};

// ─── Build the local caches snapshot from Dexie (mirrors preloadPOSCaches) ──

const buildOfflineCaches = async (companyId) => {
  const accounts = await readCollection('accounts', companyId);
  const systemAccounts = {};
  for (const code of Object.values(SYSTEM_ACCOUNTS)) {
    const acc = accounts.find(a => a.code === code);
    if (acc) systemAccounts[code] = { id: acc.id, code: acc.code, name: acc.name };
  }
  const locations = await readCollection('inventory_locations', companyId);
  const defaultLocation = locations.find(l => l.isDefault) || locations[0] || { id: null, locationName: 'Main' };
  const partyAccountByCustomerId = {};
  for (const acc of accounts) {
    if (acc.linkedEntityType === 'customer' && acc.linkedEntityId) {
      partyAccountByCustomerId[acc.linkedEntityId] = { id: acc.id, code: acc.code, name: acc.name };
    }
  }
  return { systemAccounts, defaultLocation, partyAccountByCustomerId };
};

// ─── Main offline save ──────────────────────────────────────────────────

/**
 * Save a POS sale entirely locally + enqueue for cloud sync.
 *
 * Same call signature as posFastWriteHelper.savePOSTransactionFast so the
 * online/offline router can forward to whichever one matches connectivity.
 *
 * Returns the same shape: { id, docNumber, docDate, ...transactionDoc }
 */
export const savePOSTransactionOffline = async (companyId, saleData, userId, _ignoredCaches) => {
  const {
    cart, totals, payments, paymentMethod,
    customerId, customerName,
    financialYearId, cashierName,
    branchId, branchName,
    counterId, counterCode, shiftId,
    returnMode, returnOriginalId
  } = saleData;

  const tranTypeCode = returnMode ? 'SR' : 'PS';
  const docDate = localDateStr(); // POS-06: Pakistan (device) date, not UTC

  // Build local caches from Dexie (mirrors preloadPOSCaches but reads local)
  const caches = await buildOfflineCaches(companyId);
  const partyAccount = caches.partyAccountByCustomerId[customerId] || null;
  const partyAccountId = partyAccount?.id || null;
  const partyAccountCode = partyAccount?.code || null;

  // Items catalog needed for stockFlow enrichment
  const itemsCatalog = saleData.itemsCatalog || await readCollection('inventory_items', companyId);

  // Lines for transaction header
  const lines = cart.map((item, idx) => ({
    lineNumber: idx + 1,
    itemId: item.itemId,
    itemCode: item.itemCode || '',
    itemName: item.itemName || '',
    description: item.description || '',
    quantity: item.quantity,
    unitPrice: item.priceExclTax || item.unitPrice,
    unitCost: item.unitCost || 0,
    discountPercent: item.discountPercent || 0,
    discountAmount: item.discountAmount || 0,
    taxable: item.taxable !== false,
    taxRate: item.taxRate || 0,
    taxAmount: item.taxAmount || 0,
    lineTotal: item.lineTotal || 0,
    totalCost: item.totalCost || 0,
    unitOfMeasure: item.unitOfMeasure || 'Pcs',
    unitCode: item.unitCode || item.unitOfMeasure || '',
    ...twoUnitsFor(item, itemsCatalog)
  }));

  // Doc number from pre-allocated local range (or pre-allocated by caller)
  const docNumber = saleData.preAllocatedDocNumber
    || await getNextDocNumber({
      companyId, tranTypeCode,
      counter: counterId ? { id: counterId, counterCode } : null,
      isOnline: false
    });

  const transactionId = localId();
  const timestamp = new Date().toISOString();

  const transactionDoc = strip({
    id: transactionId,
    tranTypeCode, docNumber, docDate, financialYearId,
    branchId: branchId || null, branchName: branchName || null,
    counterId: counterId || null, counterCode: counterCode || null, shiftId: shiftId || null,
    posDoc: true, // POS-03: saved from the POS counter (sale or return)
    partyType: 'customer', partyId: customerId, partyName: customerName,
    partyAccountId, partyAccountCode,
    subtotal: totals.subtotal,
    discountPercent: 0,
    discountAmount: totals.totalDiscount,
    taxableAmount: totals.subtotal - totals.totalDiscount,
    taxAmount: totals.taxAmount,
    whtRate: 0, whtAmount: 0, whtAccountId: null,
    totalAmount: totals.totalAmount,
    grossAmount: totals.subtotal,
    totalDiscount: totals.totalDiscount,
    totalGST: totals.taxAmount,
    totalWHT: 0,
    netAmount: totals.totalAmount,
    paidAmount: returnMode ? 0 : totals.totalAmount,
    balanceAmount: 0,
    status: 'Posted',
    parentId: returnOriginalId || null,
    childIds: [],
    locationId: saleData.locationId || caches.defaultLocation?.id || null,
    locationName: saleData.locationName || caches.defaultLocation?.locationName || null,
    notes: returnMode ? `POS Return against receipt` : 'POS Counter Sale',
    internalNotes: '',
    lines,
    posPayments: payments,
    paymentMethod,
    cashierName: cashierName || '',
    posTransaction: true,
    postedAt: timestamp, postedBy: userId,
    createdAt: timestamp, createdBy: userId,
    updatedAt: timestamp, updatedBy: userId,
    // Markers for the sync worker
    _offlineCreated: true,
    _offlineCreatedAt: timestamp
  });

  const baseSub = {
    transactionId, tranTypeCode, docNumber, docDate, financialYearId,
    branchId: branchId || null, branchName: branchName || null,
    counterId: counterId || null, counterCode: counterCode || null, shiftId: shiftId || null,
    status: 'Posted', createdAt: timestamp, createdBy: userId
  };
  const subEntries = tranTypeCode === 'PS'
    ? buildPOSSaleEntries(baseSub, { ...transactionDoc, partyAccountId, partyAccountCode }, caches.systemAccounts)
    : buildPOSReturnEntries(baseSub, { ...transactionDoc, partyAccountId, partyAccountCode }, caches.systemAccounts);

  if (subEntries.length > 0) {
    const dr = subEntries.reduce((s, e) => s + (Number(e.debitAmount) || 0), 0);
    const cr = subEntries.reduce((s, e) => s + (Number(e.creditAmount) || 0), 0);
    if (Math.abs(dr - cr) > 0.01) {
      throw new Error(
        `POS accounting unbalanced (offline) for ${tranTypeCode} ${docNumber}: ` +
        `Dr ${dr.toFixed(2)} ≠ Cr ${cr.toFixed(2)}.`
      );
    }
  }

  const baseStock = { transactionId, docNumber, createdAt: timestamp, createdBy: userId };
  const stockRows = buildStockFlowRows(baseStock, {
    tranTypeCode, docDate, financialYearId, branchId, branchName,
    locationId: saleData.locationId || caches.defaultLocation?.id || null,
    locationName: saleData.locationName || caches.defaultLocation?.locationName || null,
    status: 'Posted', lines
  }, itemsCatalog, caches.defaultLocation);

  // Single Dexie transaction: header + sub-trans + stock-flow + outbox
  await savePOSSaleLocally(companyId, {
    transaction: transactionDoc,
    subTransactions: subEntries,
    stockFlow: stockRows
  });

  return {
    id: transactionId,
    docNumber,
    docDate,
    ...transactionDoc
  };
};
